/**
 * ビューの中の導線が共有する配色と、案件番号の渡し方。導線そのものは機能ごとのファイル
 * （業務・業務ルールへは `business-logic.tsx`、業務フローへは `flow.tsx`）が持つ。
 *
 * **導線はビューの中のリンクだけで作る。** サイドバー（`nav-groups.tsx`）とルートの一覧
 * （`route-paths.ts`）は触らない。レーンに並ぶのは要素そのものなので、次に開く先は「いま見て
 * いる要素」から決まる — 行き先をサイドバーの行として足すと、どの要素の話なのかを利用者が
 * もう一度選び直すことになる。
 *
 * **行き先はルート定数から引く。パスの綴りをここへ書き写さない。** 画面のパスが替わっても
 * （2026-09-30 に業務のデータの流れの図を業務詳細のパスへ移した）、定数を見ているリンクはそのまま
 * 追随する。書き写すと、替えたときにこちらだけ古い綴りを指したまま 404 になる。
 *
 * **案件は画面が受け取った番号をそのまま渡す。** ノードのリンク（`SpecNodeLink`）は現在地から
 * 案件を読むが、業務全体像・案件の変更一覧は案件を prop で受け取っている（行き先の業務マップ
 * `/business-map` と業務詳細 `/businesses/$businessId` はどちらも `?pj=` を読んで案件を重ねた
 * 姿を描く）。現在地から読むと、画面が描いている姿の案件とリンクが渡す案件が食い違う — 案件を
 * 当てた姿を見ているのにマスターの業務詳細が開く。読む場所を画面の
 * 入力 1 つに揃える。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business ビューの中のリンクの配色と、案件番号をリンクの検索条件へ直す口を持つ。
 * @graph-connects none
 */

/** ビューの中のリンクの配色。行き先が違っても同じ見た目で揃える。 @graph-connects none */
export const VIEW_LINK_CLASS = 'text-[var(--color-accent-primary)] underline underline-offset-2';

/**
 * 案件番号を、リンクの検索条件が受け取る形へ直す。
 *
 * 契約の案件番号は number で、リンクの検索条件を組む口（`pjLinkSearch`）は文字列で受ける
 * （数字だけの番号は、そこで数値へ直して載せる）。変換をリンクごとに書くと、片方だけ `String` を
 * 忘れても型が通り（テンプレート埋め込みで文字列になる）、条件の綴りが経路によって変わる。
 *
 * @param pjNumber 重ねる案件番号。master を見ているなら undefined
 * @returns 検索条件に載せる案件番号。master なら undefined
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 案件番号の数値を、リンクの検索条件を組む口が受け取る文字列へ直す。
 * @graph-connects none
 */
export function pjLinkValue(pjNumber: number | undefined): string | undefined {
  return pjNumber === undefined ? undefined : String(pjNumber);
}
